import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchHrMyToday } from "../../store/hr/hrSlice";
import { HrMyToday } from "../../types/HrTypes";
import { isSuperAdminAccess } from "../../utils/employeeRoles";

const REMIND_EVERY_MS = 5 * 60 * 1000;

type ReminderKind = "punch" | "portal";

type ReminderVariant = {
  id: string;
  layout: "card" | "banner" | "ticket" | "sticky" | "pop";
  accent: string;
  accentSoft: string;
  emoji: string;
  title: (kind: ReminderKind, name: string) => string;
  body: (kind: ReminderKind, name: string) => string;
  cta: string;
};

const VARIANTS: ReminderVariant[] = [
  {
    id: "nudge",
    layout: "card",
    accent: "#345952",
    accentSoft: "#E4F0EC",
    emoji: "👋",
    title: (k, name) =>
      k === "portal"
        ? `Hey ${name} — mark yourself present!`
        : `Hey ${name} — punch in first!`,
    body: (k, name) =>
      k === "portal"
        ? `${name}, you started work without marking present. Hit Mark present on your dashboard, then tap Got it.`
        : `${name}, please punch in at the machine to start your day, then tap Got it.`,
    cta: "Got it",
  },
  {
    id: "clock",
    layout: "banner",
    accent: "#C45C26",
    accentSoft: "#FCEDE4",
    emoji: "⏰",
    title: (k, name) =>
      k === "portal"
        ? `${name}: still unmarked`
        : `${name}: no punch yet`,
    body: (k, name) =>
      k === "portal"
        ? `${name}, your day is rolling and present isn’t marked. Mark present, then press I’m on it.`
        : `${name}, please punch in at the machine — there’s no punch yet. Then press I’m on it.`,
    cta: "I’m on it",
  },
  {
    id: "ticket",
    layout: "ticket",
    accent: "#2F5D8C",
    accentSoft: "#E8F1FA",
    emoji: "🎫",
    title: (k, name) =>
      k === "portal"
        ? `${name} · mark present`
        : `${name} · punch in`,
    body: (k, name) =>
      k === "portal"
        ? `${name}, please mark present when you start work. Do that on the dashboard, then stamp OK below.`
        : `${name}, please punch in when you start work. Do that at the machine, then stamp OK below.`,
    cta: "Stamp OK",
  },
  {
    id: "sticky",
    layout: "sticky",
    accent: "#7A5B1E",
    accentSoft: "#F7F0DC",
    emoji: "📌",
    title: (k, name) =>
      k === "portal"
        ? `${name} — mark present`
        : `${name} — punch in`,
    body: (k, name) =>
      k === "portal"
        ? `${name}: start work → mark present. Then peel this off with Done.`
        : `${name}: please punch in at the machine to start work. Then peel this off with Done.`,
    cta: "Done",
  },
  {
    id: "pop",
    layout: "pop",
    accent: "#6B3FA0",
    accentSoft: "#F1E8FA",
    emoji: "⚡",
    title: (k, name) =>
      k === "portal"
        ? `${name}: present missing`
        : `${name}: punch missing`,
    body: (k, name) =>
      k === "portal"
        ? `${name}, mark present if you’ve started work. Then smash Let’s go.`
        : `${name}, please punch in if you’ve started work. Then smash Let’s go.`,
    cta: "Let’s go",
  },
  {
    id: "cheer",
    layout: "card",
    accent: "#1F6B52",
    accentSoft: "#D8F0E6",
    emoji: "🚀",
    title: (k, name) =>
      k === "portal"
        ? `${name} — mark present`
        : `${name} — punch in`,
    body: (k, name) =>
      k === "portal"
        ? `${name}, a great day starts with marking present. Do it on the dashboard, then hit Ready.`
        : `${name}, please punch in at the machine to start strong, then hit Ready.`,
    cta: "Ready",
  },
];

function pickVariant(excludeId?: string | null): ReminderVariant {
  const pool =
    excludeId && VARIANTS.length > 1
      ? VARIANTS.filter((v) => v.id !== excludeId)
      : VARIANTS;
  return pool[Math.floor(Math.random() * pool.length)]!;
}

function hasPunchedIn(today: HrMyToday | null | undefined) {
  if (!today) return false;
  if (today.attendance?.punch_in_at) return true;
  return (today.punches?.length || 0) > 0;
}

function shouldRemind(today: HrMyToday | null | undefined) {
  if (!today) return false;
  if (today.punch_modal_bypassed) return false;
  if (today.holiday) return false;
  const status = String(today.attendance?.status || "").toLowerCase();
  if (
    status === "on_leave" ||
    status === "holiday" ||
    status === "unpaid" ||
    status === "weekend" ||
    status === "wfh" ||
    status === "half_day" ||
    status === "present"
  ) {
    return false;
  }
  return !hasPunchedIn(today);
}

function ReminderShell({
  variant,
  kind,
  empName,
  checking,
  onOk,
}: {
  variant: ReminderVariant;
  kind: ReminderKind;
  empName: string;
  checking: boolean;
  onOk: () => void;
}) {
  const title = variant.title(kind, empName);
  const body = variant.body(kind, empName);

  if (variant.layout === "banner") {
    return (
      <div
        className="w-full max-w-lg overflow-hidden rounded-[28px] shadow-2xl"
        style={{ background: variant.accent }}
      >
        <div className="px-6 pb-3 pt-5 text-center text-5xl leading-none">{variant.emoji}</div>
        <div
          className="rounded-t-[32px] bg-white px-6 pb-6 pt-7"
          style={{ boxShadow: `0 -8px 0 ${variant.accentSoft}` }}
        >
          <p
            className="mb-2 inline-block rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em]"
            style={{ background: variant.accentSoft, color: variant.accent }}
          >
            Friendly nudge
          </p>
          <h2
            id="punch-in-reminder-title"
            className="mt-2 text-3xl font-black leading-tight tracking-tight text-[#1F1F1C]"
          >
            {title}
          </h2>
          <p className="mt-3 text-base font-medium leading-7 text-[#4A4A48]">{body}</p>
          <button
            type="button"
            disabled={checking}
            onClick={onOk}
            className="mt-6 w-full rounded-2xl px-5 py-4 text-lg font-black text-white shadow-md transition hover:brightness-110 disabled:opacity-60"
            style={{ background: variant.accent }}
          >
            {checking ? "Checking…" : variant.cta}
          </button>
        </div>
      </div>
    );
  }

  if (variant.layout === "ticket") {
    return (
      <div className="w-full max-w-md">
        <div
          className="relative overflow-hidden rounded-[24px] border-4 border-dashed bg-white p-6 shadow-2xl"
          style={{ borderColor: variant.accent, background: variant.accentSoft }}
        >
          <div className="absolute -left-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-black/55" />
          <div className="absolute -right-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-black/55" />
          <div className="flex items-start gap-3">
            <span className="text-4xl leading-none">{variant.emoji}</span>
            <div>
              <p
                className="text-[11px] font-black uppercase tracking-[0.18em]"
                style={{ color: variant.accent }}
              >
                Admit one · today
              </p>
              <h2
                id="punch-in-reminder-title"
                className="mt-1 text-2xl font-black leading-tight text-[#1F1F1C]"
              >
                {title}
              </h2>
            </div>
          </div>
          <p className="mt-4 border-t-2 border-dashed border-[#C8C8C3] pt-4 text-base font-semibold leading-7 text-[#3A3A38]">
            {body}
          </p>
          <button
            type="button"
            disabled={checking}
            onClick={onOk}
            className="mt-5 w-full rounded-xl border-4 border-[#1F1F1C] bg-white px-5 py-3.5 text-lg font-black text-[#1F1F1C] shadow-[4px_4px_0_#1F1F1C] transition hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1F1F1C] disabled:opacity-60"
          >
            {checking ? "Checking…" : variant.cta}
          </button>
        </div>
      </div>
    );
  }

  if (variant.layout === "sticky") {
    return (
      <div className="w-full max-w-sm">
        <div
          className="relative rotate-[-2deg] rounded-[4px] px-6 pb-6 pt-8 shadow-2xl"
          style={{
            background: variant.accentSoft,
            boxShadow: `8px 12px 0 ${variant.accent}33, 0 18px 40px rgba(0,0,0,0.25)`,
          }}
        >
          <div
            className="absolute left-1/2 top-2 h-3 w-16 -translate-x-1/2 rounded-sm opacity-70"
            style={{ background: variant.accent }}
          />
          <div className="text-center text-4xl">{variant.emoji}</div>
          <h2
            id="punch-in-reminder-title"
            className="mt-3 text-center text-2xl font-black leading-snug text-[#3A2E14]"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          >
            {title}
          </h2>
          <p
            className="mt-3 text-center text-[15px] font-medium leading-7 text-[#5A4A28]"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          >
            {body}
          </p>
          <button
            type="button"
            disabled={checking}
            onClick={onOk}
            className="mt-5 w-full rounded-full px-5 py-3.5 text-base font-black text-white disabled:opacity-60"
            style={{ background: variant.accent }}
          >
            {checking ? "Checking…" : variant.cta}
          </button>
        </div>
      </div>
    );
  }

  if (variant.layout === "pop") {
    return (
      <div className="w-full max-w-md animate-[punchPop_0.35s_ease-out]">
        <style>{`
          @keyframes punchPop {
            0% { transform: scale(0.82) rotate(-3deg); opacity: 0; }
            70% { transform: scale(1.04) rotate(1deg); opacity: 1; }
            100% { transform: scale(1) rotate(0deg); opacity: 1; }
          }
        `}</style>
        <div
          className="overflow-hidden rounded-[32px] border-4 bg-white shadow-2xl"
          style={{ borderColor: variant.accent }}
        >
          <div
            className="flex items-center gap-3 px-5 py-4"
            style={{ background: variant.accent }}
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 text-3xl">
              {variant.emoji}
            </span>
            <h2
              id="punch-in-reminder-title"
              className="text-2xl font-black leading-tight text-white"
            >
              {title}
            </h2>
          </div>
          <div className="px-5 py-5" style={{ background: variant.accentSoft }}>
            <p className="text-base font-bold leading-7 text-[#3A3A38]">{body}</p>
            <button
              type="button"
              disabled={checking}
              onClick={onOk}
              className="mt-5 w-full rounded-2xl px-5 py-4 text-lg font-black text-white shadow-[0_8px_0_rgba(0,0,0,0.18)] transition active:translate-y-1 active:shadow-none disabled:opacity-60"
              style={{ background: variant.accent }}
            >
              {checking ? "Checking…" : variant.cta}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // default chunky card
  return (
    <div className="w-full max-w-md">
      <div
        className="rounded-[28px] border-4 bg-white p-6 shadow-[10px_10px_0_rgba(0,0,0,0.18)]"
        style={{ borderColor: variant.accent }}
      >
        <div
          className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl text-4xl"
          style={{ background: variant.accentSoft }}
        >
          {variant.emoji}
        </div>
        <h2
          id="punch-in-reminder-title"
          className="text-3xl font-black leading-tight tracking-tight text-[#1F1F1C]"
        >
          {title}
        </h2>
        <p className="mt-3 text-base font-semibold leading-7 text-[#4A4A48]">{body}</p>
        <div
          className="mt-5 rounded-2xl px-4 py-3 text-sm font-bold"
          style={{ background: variant.accentSoft, color: variant.accent }}
        >
          {kind === "portal"
            ? "Start work → Mark present → then OK"
            : "Start work → Punch in → then OK"}
        </div>
        <button
          type="button"
          disabled={checking}
          onClick={onOk}
          className="mt-5 w-full rounded-2xl px-5 py-4 text-lg font-black text-white shadow-[0_6px_0_rgba(0,0,0,0.2)] transition hover:brightness-110 active:translate-y-1 active:shadow-none disabled:opacity-60"
          style={{ background: variant.accent }}
        >
          {checking ? "Checking…" : variant.cta}
        </button>
      </div>
    </div>
  );
}

const PunchInReminder = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const employee = useAppSelector((state) => state.allocationAuth.employee);
  const portalRole = useAppSelector((state) => state.auth.user?.role);
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [variantId, setVariantId] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const openRef = useRef(false);
  const lastVariantRef = useRef<string | null>(null);

  const onDashboard = location.pathname.startsWith("/dashboard");
  const empId = employee?.emp_id ?? null;
  const usesPortalMark =
    Boolean(employee?.is_remote) ||
    Boolean(employee?.is_hybrid) ||
    Boolean(employee?.can_self_mark);
  const skipReminder = isSuperAdminAccess(portalRole, employee?.role);
  const kind: ReminderKind = usesPortalMark ? "portal" : "punch";
  const empName = String(employee?.emp_name || "").trim() || "there";

  const variant = useMemo(() => {
    if (!variantId) return VARIANTS[0]!;
    return VARIANTS.find((v) => v.id === variantId) || VARIANTS[0]!;
  }, [variantId]);

  const clearTimer = () => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const openWithRandomVariant = useCallback(() => {
    const next = pickVariant(lastVariantRef.current);
    lastVariantRef.current = next.id;
    setVariantId(next.id);
    setOpen(true);
    openRef.current = true;
    clearTimer();
  }, []);

  const checkPunch = useCallback(
    async (showIfMissing: boolean) => {
      if (!empId || skipReminder) return;
      const result = await dispatch(fetchHrMyToday());
      if (!fetchHrMyToday.fulfilled.match(result)) return;
      const today = result.payload;
      if (!shouldRemind(today)) {
        setOpen(false);
        openRef.current = false;
        clearTimer();
        return;
      }
      if (showIfMissing && !openRef.current) {
        openWithRandomVariant();
      }
    },
    [dispatch, empId, openWithRandomVariant, skipReminder],
  );

  const scheduleRemind = useCallback(() => {
    clearTimer();
    if (!empId || skipReminder) return;
    timerRef.current = window.setTimeout(() => {
      void checkPunch(true);
    }, REMIND_EVERY_MS);
  }, [checkPunch, empId, skipReminder]);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const blockEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") event.stopPropagation();
    };
    window.addEventListener("keydown", blockEscape, true);
    return () => window.removeEventListener("keydown", blockEscape, true);
  }, [open]);

  useEffect(() => {
    if (!empId || !onDashboard || skipReminder) {
      setOpen(false);
      openRef.current = false;
      clearTimer();
      return;
    }

    let cancelled = false;
    const start = async () => {
      const result = await dispatch(fetchHrMyToday());
      if (cancelled) return;
      if (!fetchHrMyToday.fulfilled.match(result) || !shouldRemind(result.payload)) {
        clearTimer();
        return;
      }
      // Show immediately on login / dashboard entry when punch is missing.
      if (!openRef.current) {
        openWithRandomVariant();
      }
    };
    void start();

    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [dispatch, empId, onDashboard, openWithRandomVariant, skipReminder]);

  const onOk = async () => {
    setChecking(true);
    try {
      setOpen(false);
      openRef.current = false;
      const result = await dispatch(fetchHrMyToday());
      if (!fetchHrMyToday.fulfilled.match(result) || !shouldRemind(result.payload)) {
        clearTimer();
        return;
      }
      scheduleRemind();
    } finally {
      setChecking(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="punch-in-reminder-title"
    >
      <ReminderShell
        variant={variant}
        kind={kind}
        empName={empName}
        checking={checking}
        onOk={() => void onOk()}
      />
    </div>
  );
};

export default PunchInReminder;
